{ceil, floor, max, min, round, sin} := Math

{
  Container
  Graphics
  ObservablePoint
  Sprite
} from "pixi.js"

{ HUD_HEALTH_COLORS, HUD_TIER_COLORS } from ./const.civet
{ ArcadeDigits, arcadeTextTexture, beatPose, FlameLayer, mixColor } from ./arcade-fx.civet

// In-canvas Game Mode HUD: score, health bar, combo readout, and combo
// meter over whichever view is active. Pixi rather than DOM (superseding
// the old views/game-hud.civet) so it rides the same canvas as the views —
// it shows up in video exports for free and the canvas effect toolbox is
// open to it. Palette in const.civet (HUD_TIER_COLORS / HUD_HEALTH_COLORS);
// a translucent panel keeps it legible on the staff's light field and the
// rolls' dark one alike. Lettering is the arcade-fx chunky treatment —
// big, extruded, glossy — matching the judgment popups.

W := 232
H := 128
BAR_W := W - 20
// both meters identical: 14px wells at these y's, 2px border, 10px fluid
BAR_H := 14
HEALTH_Y := 46
METER_Y := H - 20
SCORE_GOLD := 0xFFD166

shade := (c: number, t: number) ->
  r := ((c >> 16 & 255) * t) | 0
  g := ((c >> 8 & 255) * t) | 0
  b := ((c & 255) * t) | 0
  (r << 16) | (g << 8) | b

CELL := 3

// Pixel-fluid fill for the HUD bars: the body quantized to whole cells, the
// leading edge sloshing in stepped tongues, bubbles climbing through the
// middle — liquid in a tank drawn entirely in integer rects of opaque color,
// nothing anti-aliased, nothing translucent
drawPixelFluid := (g: Graphics, x: number, y: number, w: number, h: number, frac: number, color: number, pose: number) ->
  return unless frac > 0
  edge := min w, ceil(w * frac / CELL) * CELL
  body := max 0, edge - CELL * 2
  rows := max 2, floor h / 5
  rh := floor h / rows
  rowH := (r: number) -> if r is rows - 1 then h - r * rh else rh
  rowColor := (r: number) -> if r is 0 then color else shade color, 0.72
  // body strips: bright surface row over darker depths
  for r of [0...rows]
    g.beginFill rowColor(r), 1
    g.drawRect x, y + r * rh, body, rowH r
    g.endFill()
  // the sloshing surface: per-row tongue lengths cycling out of phase
  for r of [0...rows]
    len := min CELL * (1 + (pose + r * 3 + r % 2) % 3), w - body
    if len > 0
      g.beginFill rowColor(r), 1
      g.drawRect x + body, y + r * rh, len, rowH r
      g.endFill()
  // hard sheen: an opaque lighter pixel row along the top — no alpha haze
  g.beginFill mixColor(color, 0xFFFFFF, 0.55), 1
  g.drawRect x, y, max(0, body - 2), 2
  g.endFill()
  // bubbles climb the body one row per pose, then respawn elsewhere
  if body > 12
    for k of [0...max 1, floor body / 48]
      ph := pose + k * 7
      bx := (floor(ph / rows) * 53 + k * 61) % (body - 8)
      brow := rows - 1 - ph % rows
      g.beginFill mixColor(color, 0xFFFFFF, 0.45), 1
      g.drawRect x + 3 + bx, y + brow * rh + 1, 2, 2
      g.endFill()
  // a stray droplet thrown ahead of the surface every few poses
  if pose % 4 is 0 and edge + 8 < w
    g.beginFill color, 1
    g.drawRect x + edge + 4, y + (pose * 13) % max(1, h - 3), 3, 3
    g.endFill()
  return

export GameHudLayer := (screenHalfSize: ObservablePoint, player: Player) ->
  container := new Container
  container.visible = false

  panel := new Graphics
  panel.lineStyle 2, 0xFFFFFF, 0.12
  panel.beginFill 0x000000, 0.6
  panel.drawRoundedRect 0, 0, W, H, 10
  panel.endFill()
  container.addChild panel

  // score: big gold arcade digits, right-aligned, punching on every gain
  scoreRow := ArcadeDigits container, fontSize: 30, color: SCORE_GOLD
  scoreRow.container.y = 6
  scoreW .= 0

  // health bar + combo meter, redrawn each frame
  bars := new Graphics
  container.addChild bars

  // the combo meter catches fire at high tiers (pixel flames, panel-local)
  meterFlames := FlameLayer container, additive: false, cap: 20

  // combo: big tier-colored count with a small COMBO tag riding it
  comboRow := new Container
  container.addChild comboRow
  comboDigits := ArcadeDigits comboRow, fontSize: 26, color: 0xFFFFFF
  comboTag := new Sprite
  comboRow.addChild comboTag
  comboRow.y = 74
  comboW .= 0

  // multiplier badge, lives at the panel's left edge opposite the combo
  multBadge := new Sprite
  multBadge.y = 78
  multBadge.x = 10
  container.addChild multBadge

  // now-clock of the last combo advance / score gain, for scale pops — the
  // same clock render() is later called with (fxNow in an offline render)
  popAt .= -Infinity
  player.gameHudCombo.observe (combo: number) ->
    popAt = player.fxNow?() ?? performance.now() if combo >= 2
  scorePopAt .= -Infinity
  lastScore .= -1

  render := (now: number) ->
    active := player.playMode() is "game"
    container.visible = active
    return unless active

    // top-right of the canvas, below the mode hud's play/gear cluster
    {x: shw} := screenHalfSize
    container.x = 2 * shw - 16 - W
    container.y = 64

    // rows re-set every frame — glyph textures are cached map lookups, and
    // resolving fresh means a tuning change (fx-lab) shows up immediately
    score := player.gameHudScore()
    scorePopAt = now if lastScore >= 0 and score > lastScore
    lastScore = score
    scoreW = scoreRow.set score.toLocaleString()
    // punch from the right edge on every gain: Tartakovsky rules — snap to
    // the extreme pose, hold it, snap back; never tween through the middle
    scorePop := if now - scorePopAt < 90 then 1.14 else 1
    scoreRow.container.scale.set scorePop
    scoreRow.container.x = W - 10 - scoreW * scorePop

    combo := player.gameHudCombo()
    mult := player.gameHudMultiplier()
    tier := min mult, HUD_TIER_COLORS.length - 1

    comboDigits.setColor HUD_TIER_COLORS[tier]
    comboTag.texture = arcadeTextTexture "combo", 11, HUD_TIER_COLORS[tier]
    comboW = comboDigits.set `${combo}×`
    comboTag.x = comboW + 6
    comboTag.y = 8
    comboRow.visible = combo >= 2
    if comboRow.visible
      // pose snap on every advance — big pose held, one intermediate, rest.
      // No idle pulsing or alpha shimmer: between hits it stands rock solid
      // (crisp-over-blurry; the old sine pulse read as limp)
      dt := now - popAt
      // the extreme pose is a smear frame — stretched wide, not just bigger
      [popX, popY] :=
        if dt < 90 then [1.34, 1.14]
        else if dt < 160 then [1.08, 1.08]
        else [1, 1]
      comboRow.scale.set popX, popY
      // anchored at the right edge, like the DOM HUD's
      // transform-origin: right center
      comboRow.x = W - 10 - (comboW + 6 + comboTag.width) * popX
      comboRow.alpha = 1

    multBadge.texture = arcadeTextTexture `×${mult}`, 17, HUD_TIER_COLORS[tier]
    multBadge.visible = mult > 1

    health := player.gameHudHealth()
    healthColor :=
      if health > 0.5 then HUD_HEALTH_COLORS.high
      else if health > 0.25 then HUD_HEALTH_COLORS.mid
      else HUD_HEALTH_COLORS.low
    // low health panic-flashes the border (the old health-panic keyframes)
    borderColor :=
      if health <= 0.25 and sin(now / 95) > 0
        HUD_HEALTH_COLORS.low
      else
        0xFFFFFF

    // both bars slosh on the 32nd-note grid (wall-clock cadence off transport)
    pose := beatPose now, 8, 120

    bars.clear()
    // wells: square 2px borders as nested integer rects — no rounded
    // corners, no line strokes, nothing fractional to anti-alias
    well := (y: number, edgeColor: number, edgeAlpha: number) ->
      bars.beginFill edgeColor, edgeAlpha
      bars.drawRect 10, y, BAR_W, BAR_H
      bars.endFill()
      bars.beginFill 0x000000, 0.85
      bars.drawRect 12, y + 2, BAR_W - 4, BAR_H - 4
      bars.endFill()

    // health
    well HEALTH_Y, borderColor, 0.6
    drawPixelFluid bars, 12, HEALTH_Y + 2, BAR_W - 4, BAR_H - 4, health, healthColor, pose
    // segment ticks every 10%, 2px, integer positions
    bars.beginFill 0x000000, 0.4
    for k of [1...10]
      bars.drawRect 12 + round((BAR_W - 4) * k / 10), HEALTH_Y + 2, 2, BAR_H - 4
    bars.endFill()

    // combo meter: same well as health; fills toward the next multiplier
    // tier, pegged full at ×4, fluid out of phase with the health bar
    fill := if mult >= 4 then 1 else (combo % 10) / 10
    well METER_Y, 0xFFFFFF, 0.3
    drawPixelFluid bars, 12, METER_Y + 2, BAR_W - 4, BAR_H - 4, fill, HUD_TIER_COLORS[tier], pose + 3
    fw := (BAR_W - 4) * fill
    if fw > 8 and tier >= 2
      // sparkle: pixel glints that snap position on the beat grid — each
      // holds its spot for a full pose, then jumps
      glints := if tier >= 3 then 4 else 2
      bars.beginFill 0xFFFFFF, 1
      for k of [0...glints]
        continue unless (pose + k) % 3
        gx := 14 + (pose * 37 + k * 53) % max(1, floor fw - 6)
        bars.drawRect gx, METER_Y + 3 + (pose * 7 + k * 5) % (BAR_H - 8), 2, 2
      bars.endFill()
    if fw > 8 and tier >= 3
      // …and at tier 3+ the meter itself catches fire, harder at ×4
      if Math.random() < (if tier >= 4 then 0.7 else 0.35)
        meterFlames.emit 14 + Math.random() * (fw - 8), METER_Y + 2, 0.45 + Math.random() * 0.2
    meterFlames.step now

  {container, render}

export default GameHudLayer
